// Global state composables — jobs + config loaded once, shared across pages.
import { reactive, computed } from 'vue';
import { api } from './api';
import type { Job } from '@qjob/shared';
import {
  getJobRowKey,
  isJobPaymentOverdue,
  normalizeJobStatus,
  parseEventDateStart,
} from './utils';

interface JobsState {
  jobs: Job[];
  sheets: string[];
  loading: boolean;
  saving: boolean;
  error: string;
  lastLoadedAt: number;
}

const state = reactive<JobsState>({
  jobs: [],
  sheets: [],
  loading: false,
  saving: false,
  error: '',
  lastLoadedAt: 0,
});

export function useJobsStore() {
  async function reload() {
    state.loading = true;
    state.error = '';
    try {
      const result = await api.getJobs();
      state.jobs = result.jobs;
      state.sheets = result.sheets;
      state.lastLoadedAt = Date.now();
    } catch (e: any) {
      state.error = String(e?.message || e);
    } finally {
      state.loading = false;
    }
  }

  async function save(job: Partial<Job>, options: Record<string, any> = {}) {
    state.saving = true;
    try {
      const result = await api.saveJob(job, options);
      // Replace in-memory copy or add new.
      const canonical: Job = {
        ...result.job,
        _rowIndex: result.rowIndex,
        _sheetName: result.sheetName,
      };
      const canonicalKey = getJobRowKey(canonical);
      const idx = state.jobs.findIndex((j) => getJobRowKey(j) === canonicalKey);
      if (idx >= 0) state.jobs.splice(idx, 1, canonical);
      else state.jobs.unshift(canonical);
      return result;
    } finally {
      state.saving = false;
    }
  }

  async function remove(job: Partial<Job>) {
    state.saving = true;
    const rowKey = getJobRowKey(job);
    const sheetName = String(job._sheetName || '').trim();
    const deletedRowIndex = Number(job._rowIndex || 0);
    try {
      const result = await api.deleteJob(job);
      const idx = state.jobs.findIndex((j) => getJobRowKey(j) === rowKey);
      if (idx >= 0) {
        state.jobs.splice(idx, 1);
        if (sheetName && deletedRowIndex > 1) {
          for (const remaining of state.jobs) {
            if (
              remaining._sheetName === sheetName &&
              Number(remaining._rowIndex || 0) > deletedRowIndex
            ) {
              remaining._rowIndex = Number(remaining._rowIndex) - 1;
            }
          }
        }
      } else {
        await reload();
      }
      return result;
    } catch (error) {
      // A stale row can be rejected by the API. Refresh so the next action
      // cannot reuse row indexes from before another deletion.
      await reload();
      throw error;
    } finally {
      state.saving = false;
    }
  }

  const overdueJobs = computed(() => state.jobs.filter((j) => isJobPaymentOverdue(j)));

  return {
    state,
    reload,
    save,
    remove,
    overdueJobs,
  };
}

interface ConfigState {
  config: Record<string, string>;
  loading: boolean;
  saving: boolean;
  error: string;
}

const configState = reactive<ConfigState>({
  config: {},
  loading: false,
  saving: false,
  error: '',
});

export function useConfigStore() {
  async function reload() {
    configState.loading = true;
    configState.error = '';
    try {
      const result = await api.getConfig();
      configState.config = result.config;
    } catch (e: any) {
      configState.error = String(e?.message || e);
    } finally {
      configState.loading = false;
    }
  }

  async function save(config: Record<string, any>) {
    configState.saving = true;
    try {
      const result = await api.saveConfig(config);
      Object.assign(configState.config, config);
      return result;
    } finally {
      configState.saving = false;
    }
  }

  return { state: configState, reload, save };
}

// Derived stats for dashboard.
export function useJobsStats() {
  const { state } = useJobsStore();
  return computed(() => {
    const now = new Date();
    const thisMonth = state.jobs.filter((j) => {
      const d = parseEventDateStart(j['Event Date']);
      return (
        d && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
      );
    }).length;
    const paid = state.jobs.filter((j) => normalizeJobStatus(j.Status) === 'เสร็จสิ้น').length;
    const unpaid = state.jobs.filter((j) => normalizeJobStatus(j.Status) === 'รอชำระ').length;
    return { total: state.jobs.length, paid, unpaid, thisMonth };
  });
}
